import { Color3, Mesh, MeshBuilder, StandardMaterial, TransformNode, type Scene } from '@babylonjs/core';
import { SeededRng, type RiftProgressionState } from '@sorrem/shared';

const POOL_SIZE = 18;
const MIN_ACTIVE_FRACTION = 0.15;

interface DebrisFragment {
    readonly mesh: Mesh;
    readonly orbitRadius: number;
    readonly orbitHeight: number;
    readonly angularSpeedBase: number;
    readonly tilt: number;
    angle: number;
}

const STONE_TINTS: readonly Color3[] = [
    new Color3(0.28, 0.24, 0.3),
    new Color3(0.22, 0.18, 0.32),
    new Color3(0.35, 0.28, 0.4),
];
const CRYSTAL_TINTS: readonly Color3[] = [
    new Color3(0.5, 0.3, 0.8),
    new Color3(0.3, 0.6, 0.85),
    new Color3(0.7, 0.4, 0.9),
];

export interface FloatingDebrisSystemOptions {
    readonly seed: string;
}

/**
 * Orbiting stone/crystal fragments destabilized by the rift's presence.
 * A fixed pool of meshes is built once (deterministic per-fragment orbit
 * path via `SeededRng`); `setProgression` scales how many of the pool are
 * currently active and how fast they orbit, rather than creating/destroying
 * meshes at runtime.
 */
export class FloatingDebrisSystem {
    private readonly fragments: DebrisFragment[] = [];
    private readonly root: TransformNode;
    private speedMultiplier = 1;
    private activeCount = 0;

    constructor(scene: Scene, options: FloatingDebrisSystemOptions) {
        this.root = new TransformNode('crucible-debris-root', scene);
        const rng = new SeededRng(options.seed);

        for (let index = 0; index < POOL_SIZE; index++) {
            this.fragments.push(this.buildFragment(scene, index, rng));
        }
        this.applyActiveCount(0);
    }

    private buildFragment(scene: Scene, index: number, rng: SeededRng): DebrisFragment {
        const isCrystal = rng.random() < 0.4;
        const size = 0.12 + rng.random() * 0.22;
        const mesh = isCrystal
            ? MeshBuilder.CreatePolyhedron(`crucible-debris-${index}`, { type: 3, size }, scene)
            : MeshBuilder.CreatePolyhedron(`crucible-debris-${index}`, { type: Math.floor(rng.random() * 4), size }, scene);
        mesh.parent = this.root;

        const material = new StandardMaterial(`crucible-debris-material-${index}`, scene);
        const tintPalette = isCrystal ? CRYSTAL_TINTS : STONE_TINTS;
        const tint = tintPalette[Math.floor(rng.random() * tintPalette.length)]!;
        material.diffuseColor = tint;
        material.emissiveColor = isCrystal ? tint.scale(0.35) : new Color3(0, 0, 0);
        material.specularColor = new Color3(0.1, 0.1, 0.12);
        mesh.material = material;

        mesh.rotation.set(rng.random() * Math.PI, rng.random() * Math.PI, rng.random() * Math.PI);

        return {
            mesh,
            orbitRadius: 4.5 + rng.random() * 6,
            orbitHeight: 0.5 + rng.random() * 4.5,
            angularSpeedBase: 0.08 + rng.random() * 0.18,
            tilt: (rng.random() - 0.5) * 0.6,
            angle: rng.random() * Math.PI * 2,
        };
    }

    /** Scales active fragment count and orbit speed from the current rift progression. Call whenever progression changes. */
    setProgression(progression: RiftProgressionState): void {
        const stageWeight = progression.stage === 'unstable' ? 0.25 : progression.stage === 'stabilizing' ? 0.6 : 1;
        const weight = Math.min(1, stageWeight * (0.6 + progression.stageProgress * 0.4));
        const active = Math.max(1, Math.round(POOL_SIZE * Math.max(MIN_ACTIVE_FRACTION, weight)));
        this.applyActiveCount(active);
        this.speedMultiplier = 0.5 + weight * 1.5;
    }

    private applyActiveCount(count: number): void {
        this.activeCount = count;
        for (let index = 0; index < this.fragments.length; index++) {
            this.fragments[index]!.mesh.setEnabled(index < count);
        }
    }

    /** Advances every active fragment's orbit. Call every frame with the elapsed seconds. */
    update(deltaSeconds: number): void {
        for (let index = 0; index < this.activeCount; index++) {
            const fragment = this.fragments[index]!;
            fragment.angle += deltaSeconds * fragment.angularSpeedBase * this.speedMultiplier;
            const x = Math.cos(fragment.angle) * fragment.orbitRadius;
            const z = Math.sin(fragment.angle) * fragment.orbitRadius;
            const wobble = Math.sin(fragment.angle * 3 + fragment.tilt) * 0.3;
            fragment.mesh.position.set(x, fragment.orbitHeight + wobble, z);
            fragment.mesh.rotation.x += deltaSeconds * 0.2;
            fragment.mesh.rotation.y += deltaSeconds * 0.15;
        }
    }

    dispose(): void {
        for (const fragment of this.fragments) {
            fragment.mesh.material?.dispose();
            fragment.mesh.dispose();
        }
        this.fragments.length = 0;
        this.root.dispose();
    }
}
